import { IVoucherListItem } from "../../../accounting.interface";
import {
  VoucherFilters,
  VoucherFormData,
  VoucherLineForm,
  VoucherTableData,
} from "../types";

// =============================================================================
// CONSTANTS
// =============================================================================

export const VOUCHER_TYPES = [
  {
    value: "payment",
    label: "Payment Voucher",
    color: "#EF4444",
    icon: "payment",
  },
  {
    value: "receipt",
    label: "Receipt Voucher",
    color: "#10B981",
    icon: "receipt",
  },
  {
    value: "journal",
    label: "Journal Voucher",
    color: "#3B82F6",
    icon: "book",
  },
];

export const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "card", label: "Card" },
  { value: "online", label: "Online" },
];

export const VOUCHER_STATUS = [
  { value: "draft", label: "Draft", color: "#F59E0B", icon: "edit" },
  { value: "posted", label: "Posted", color: "#10B981", icon: "check_circle" },
  { value: "reversed", label: "Reversed", color: "#EF4444", icon: "undo" },
];

// =============================================================================
// FILTER UTILITIES
// =============================================================================

export const createDefaultFilters = (): VoucherFilters => ({
  dateFrom: undefined,
  dateTo: undefined,
  selectedBranch: "organization",
  voucherType: "all",
  status: "all",
  paymentMethod: undefined,
  beneficiary: undefined,
  referenceNumber: undefined,
  narrationContains: undefined,
});

// TODO: This filter function is not needed anymore since filtering is done by the API
// Keep for backwards compatibility but mark for removal
export const filterVouchers = (
  vouchers: IVoucherListItem[],
  filters: VoucherFilters
): IVoucherListItem[] => {
  // TODO: Remove this function - filtering should be done by the API
  // This is kept temporarily for any components that might still use it
  return vouchers.filter((voucher) => {
    // Date range filter
    if (
      filters.dateFrom &&
      voucher.voucher_date < new Date(filters.dateFrom).getTime()
    )
      return false;
    if (
      filters.dateTo &&
      voucher.voucher_date > new Date(filters.dateTo).getTime()
    )
      return false;

    // Branch filter
    if (
      filters.selectedBranch &&
      filters.selectedBranch !== "organization" &&
      voucher.branch_id !== filters.selectedBranch
    )
      return false;

    // Voucher type filter
    if (
      filters.voucherType &&
      filters.voucherType !== "all" &&
      voucher.voucher_type !== filters.voucherType
    )
      return false;

    // Status filter
    if (
      filters.status &&
      filters.status !== "all" &&
      voucher.status !== filters.status
    )
      return false;

    // Voucher ID filter
    if (
      filters.referenceNumber &&
      !voucher.voucher_id
        .toLowerCase()
        .includes(filters.referenceNumber.toLowerCase())
    )
      return false;

    // Beneficiary filter
    if (
      filters.beneficiary &&
      voucher.beneficiary &&
      !voucher.beneficiary
        .toLowerCase()
        .includes(filters.beneficiary.toLowerCase())
    )
      return false;

    // Payment method filter
    if (
      filters.paymentMethod &&
      voucher.payment_method !== filters.paymentMethod
    )
      return false;

    // Narration contains filter
    if (
      filters.narrationContains &&
      !voucher.narration
        .toLowerCase()
        .includes(filters.narrationContains.toLowerCase())
    )
      return false;

    return true;
  });
};

// =============================================================================
// DATA TRANSFORMATION UTILITIES
// =============================================================================

export const transformVouchersToTableData = (
  vouchers: IVoucherListItem[]
): VoucherTableData[] => {
  return vouchers.map((voucher) => {
    // TODO: Get real branch names from branch management API
    const branchName = voucher.branch_id
      ? `Branch ${voucher.branch_id}`
      : "Organization";
    const typeConfig = getVoucherTypeConfig(voucher.voucher_type);
    const statusConfig = getStatusConfig(voucher.status);

    return {
      ...voucher,
      branchName,
      formattedAmount: formatCurrency(voucher.total_amount),
      formattedDate: formatDate(voucher.voucher_date),
      formattedVoucherType: typeConfig.label,
      formattedStatus: statusConfig.label,
    };
  });
};

export const formatCurrency = (
  amount: number,
  currency: string = "BHD"
): string => {
  return `${currency} ${amount.toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  })}`;
};

export const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// =============================================================================
// VOUCHER FORM UTILITIES
// =============================================================================

export const createInitialVoucherFormData = (): VoucherFormData => ({
  voucherType: "payment",
  voucherDate: new Date().toISOString().split("T")[0],
  narration: "",
  beneficiary: "",
  linkedEntity: "",
  paymentMethod: "cash",
  referenceNumber: "",
  branchId: "",
  voucherLines: [createEmptyVoucherLine(), createEmptyVoucherLine()],
});

export const createEmptyVoucherLine = (): VoucherLineForm => ({
  id: `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  account: undefined,
  debit: undefined,
  credit: undefined,
  description: "",
});

export const addVoucherLine = (lines: VoucherLineForm[]): VoucherLineForm[] => {
  return [...lines, createEmptyVoucherLine()];
};

export const removeVoucherLine = (
  lines: VoucherLineForm[],
  lineId: string
): VoucherLineForm[] => {
  if (lines.length <= 2) return lines; // Minimum 2 lines required
  return lines.filter((line) => line.id !== lineId);
};

export const updateVoucherLine = (
  lines: VoucherLineForm[],
  lineId: string,
  field: keyof VoucherLineForm,
  value: any
): VoucherLineForm[] => {
  return lines.map((line) =>
    line.id === lineId ? { ...line, [field]: value } : line
  );
};

export const handleVoucherAmountChange = (
  lines: VoucherLineForm[],
  lineId: string,
  field: "debit" | "credit",
  value: number | undefined
): VoucherLineForm[] => {
  return lines.map((line) => {
    if (line.id === lineId) {
      // Clear the opposite field when entering an amount
      const oppositeField = field === "debit" ? "credit" : "debit";
      return {
        ...line,
        [field]: value,
        [oppositeField]: undefined,
      };
    }
    return line;
  });
};

// =============================================================================
// VOUCHER VALIDATION UTILITIES
// =============================================================================

export const calculateVoucherTotals = (lines: VoucherLineForm[]) => {
  const totals = lines.reduce(
    (acc, line) => {
      if (line.debit) acc.totalDebits += line.debit;
      if (line.credit) acc.totalCredits += line.credit;
      return acc;
    },
    { totalDebits: 0, totalCredits: 0 }
  );

  return {
    ...totals,
    difference: Math.abs(totals.totalDebits - totals.totalCredits),
    isBalanced: Math.abs(totals.totalDebits - totals.totalCredits) < 0.001,
  };
};

export const validateVoucherForm = (formData: VoucherFormData) => {
  const errors: Record<string, string> = {};
  const lineErrors: Record<string, Record<string, string>> = {};

  // Header validation
  if (!formData.narration.trim()) {
    errors.narration = "Narration is required";
  }

  if (!formData.voucherDate) {
    errors.voucherDate = "Voucher date is required";
  }

  if (formData.voucherType !== "journal" && !formData.beneficiary?.trim()) {
    errors.beneficiary =
      "Beneficiary is required for payment and receipt vouchers";
  }

  // Line validation
  const validLines = formData.voucherLines.filter(
    (line) => line.account && (line.debit || line.credit)
  );

  if (validLines.length < 2) {
    errors.lines = "At least 2 valid lines are required";
  }

  formData.voucherLines.forEach((line) => {
    const lineError: Record<string, string> = {};

    if ((line.debit || line.credit) && !line.account) {
      lineError.account = "Account is required when amount is entered";
    }

    if (line.account && !line.debit && !line.credit) {
      lineError.amount = "Either debit or credit amount is required";
    }

    if (line.debit && line.credit) {
      lineError.amount = "Cannot have both debit and credit amounts";
    }

    if (Object.keys(lineError).length > 0) {
      lineErrors[line.id] = lineError;
    }
  });

  // Balance validation
  const totals = calculateVoucherTotals(formData.voucherLines);
  if (!totals.isBalanced && validLines.length >= 2) {
    errors.balance = `Voucher is not balanced. Difference: ${totals.difference.toFixed(
      3
    )}`;
  }

  return {
    isValid:
      Object.keys(errors).length === 0 && Object.keys(lineErrors).length === 0,
    errors,
    lineErrors,
    totals,
  };
};

// =============================================================================
// VOUCHER TRANSFORMATION UTILITIES
// =============================================================================

export const transformFormDataToVoucher = (
  formData: VoucherFormData,
  status: "draft" | "posted" = "draft"
): Partial<IVoucherListItem> => {
  const totals = calculateVoucherTotals(formData.voucherLines);
  const validLines = formData.voucherLines.filter(
    (line) => line.account && (line.debit || line.credit)
  );

  // TODO: Import proper VoucherLine interface from accounting.interface.ts
  const voucherLines: any[] = validLines.map((line) => ({
    id: line.id,
    voucher_id: "", // Will be set when voucher is created
    account_id: line.account!.id,
    account_code: line.account!.accountCode,
    account_name: line.account!.accountName,
    debit: line.debit || undefined,
    credit: line.credit || undefined,
    description: line.description || undefined,
  }));

  // TODO: Remove voucher_lines from return - IVoucherListItem doesn't include detailed lines
  // TODO: Use IVoucherDetail interface for detailed voucher operations
  return {
    voucher_type: formData.voucherType,
    voucher_date: new Date(formData.voucherDate).getTime(), // Convert to epoch milliseconds
    narration: formData.narration,
    beneficiary: formData.beneficiary || undefined,
    payment_method: formData.paymentMethod || undefined,
    reference_number: formData.referenceNumber || undefined,
    branch_id: formData.branchId || undefined,
    status,
    total_amount: Math.max(totals.totalDebits, totals.totalCredits),
    // voucher_lines: voucherLines, // TODO: Remove - not part of IVoucherListItem
  } as any; // TODO: Fix type casting when IVoucherDetail is available
};

// =============================================================================
// VOUCHER ID GENERATION
// =============================================================================

export const generateVoucherId = (
  voucherType: "payment" | "receipt" | "journal"
): string => {
  const prefix =
    voucherType === "payment" ? "PV" : voucherType === "receipt" ? "RV" : "JV";
  const year = new Date().getFullYear();
  const sequence = Math.floor(Math.random() * 9999) + 1;
  return `${prefix}-${year}-${sequence.toString().padStart(4, "0")}`;
};

// =============================================================================
// VOUCHER REVERSAL UTILITIES
// =============================================================================

// TODO: This function requires IVoucherDetail interface instead of IVoucherListItem
// TODO: IVoucherListItem doesn't have voucher_lines property - need detailed voucher data
export const createReversingVoucher = (
  originalVoucher: any // TODO: Replace with IVoucherDetail when available
): any => {
  // TODO: This function needs complete rewrite with proper IVoucherDetail interface
  // TODO: Current implementation is placeholder to prevent build errors

  return {
    voucher_type: originalVoucher.voucher_type,
    voucher_date: Date.now(), // Use current timestamp in epoch milliseconds
    narration: `Reversal of voucher ${originalVoucher.voucher_id}: ${originalVoucher.narration}`,
    beneficiary: originalVoucher.beneficiary,
    payment_method: originalVoucher.payment_method,
    branch_id: originalVoucher.branch_id,
    status: "posted" as const,
    total_amount: originalVoucher.total_amount,
    // TODO: Handle voucher_lines reversal when IVoucherDetail is available
  };
};

// =============================================================================
// EXPORT UTILITIES
// =============================================================================

export const getVoucherTypeConfig = (voucherType: string) => {
  return (
    VOUCHER_TYPES.find((type) => type.value === voucherType) || VOUCHER_TYPES[0]
  );
};

export const getStatusConfig = (status: string) => {
  return VOUCHER_STATUS.find((s) => s.value === status) || VOUCHER_STATUS[0];
};

export const getPaymentMethodLabel = (paymentMethod?: string) => {
  const method = PAYMENT_METHODS.find((m) => m.value === paymentMethod);
  return method?.label || paymentMethod || "N/A";
};
